import { useEffect, useSyncExternalStore } from 'react';
import API from '../../api/api';
import { ClipStyleSummary } from '../../api/clipstyleSchema';

// Module-level cached fetch of the ClipStyle registry so every clip card can
// read the `renderable` flag (and other summary data) without each card issuing
// its own GET /clipstyles. The Settings modal loads the same list on demand;
// this cache is the shared, per-card-friendly counterpart.

let cache: Record<string, ClipStyleSummary> | null = null;
let inflight: Promise<void> | null = null;
// Task 26 fix round 1 (I-1): a generation token so an older request can
// never overwrite what a newer one already wrote, whichever order the two
// happen to resolve in — `startLoad` is shared by the first load
// (`ensureLoaded`) and an explicit `refreshClipStyleRegistry()`, and either
// one may be in flight when the other starts.
let generation = 0;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((fn) => fn());

/**
 * Fetches the registry and stores it under `gen`. Never nulls `cache`
 * itself (I-1): a good cache must survive a failed or superseded fetch so
 * every consumer in the live studio (`OutputContractSelect`,
 * `useClipStyleRenderable`, `useClipStyleKind`, …) keeps reading the last
 * good data instead of flashing to "loading" or "empty" mid-refresh.
 */
const startLoad = (): Promise<void> => {
  const gen = ++generation;
  const p = API.getClipStyles()
    .then((styles) => {
      if (gen !== generation) return; // superseded by a newer load/refresh
      const map: Record<string, ClipStyleSummary> = {};
      styles.forEach((style) => {
        map[style.id] = style;
      });
      cache = map;
    })
    .catch(() => {
      // A transient failure only backfills `{}` when nothing has ever
      // loaded — it must never wipe a cache that was already good.
      if (gen === generation && cache === null) cache = {};
    })
    .finally(() => {
      if (gen === generation) inflight = null;
      emit();
    });
  inflight = p;
  return p;
};

const ensureLoaded = (): void => {
  if (cache || inflight) return;
  startLoad();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): Record<string, ClipStyleSummary> | null => cache;

export const useClipStyleRegistry = (): Record<string, ClipStyleSummary> | null => {
  useEffect(() => {
    ensureLoaded();
  }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

/**
 * Whether the given style renders via the Remotion renderer. `undefined` while
 * the registry is loading or for an unknown style — callers should treat only
 * an explicit `false` as "not renderable" so a slow/failed fetch never blocks
 * the Render button.
 */
export const useClipStyleRenderable = (styleId: string): boolean | undefined => {
  const registry = useClipStyleRegistry();
  const entry = registry?.[styleId];
  if (!entry) return undefined;
  return entry.renderable;
};

/** The registry `kind` of a style (e.g. "carousel"); `undefined` while loading or when it has none. */
export const useClipStyleKind = (styleId: string): string | undefined => useClipStyleRegistry()?.[styleId]?.kind;

/** Test-only reset. Bumps `generation` too, so a load left over from a
 * previous test can never resolve into the freshly reset cache. */
export const __resetClipStyleRegistry = (): void => {
  cache = null;
  inflight = null;
  generation += 1;
  listeners.clear();
};

/**
 * Loads the registry again, notifying every subscriber once the new data
 * lands — e.g. after a clipstyle approval registered a new style (Task 26's
 * ReviewPanel calls this after a successful approve, so a card/select
 * reading `useClipStyleRenderable`/`useClipStyleKind` for that style —
 * including `OutputContractSelect` — picks up the freshly registered entry
 * without a full page reload). Unlike the first load, this always re-fetches
 * even when a cache already exists — but (I-1) the OLD cache stays live
 * until the new fetch actually succeeds, and stays live forever if it
 * fails: a transient `GET /clipstyles` failure right after an approval must
 * not degrade the whole studio's registry cache to empty for the rest of
 * the tab's session.
 */
export const refreshClipStyleRegistry = (): Promise<void> => startLoad();
